import { useCallback, useEffect, useMemo } from 'react'

import { isEqual, uniqBy } from 'lodash'
import { usePrevious } from 'react-use'

import { SupportedChainId } from '~/constants/chains'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { useAddressBookData } from '~/hooks/useAddressBook'
import { useDisplayName } from '~/hooks/useDisplayName'
import { useEnsAvatar } from '~/hooks/useENS'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  updateSyncedLocalStorage,
  useSyncedLocalStorage,
} from '~/hooks/useSyncedLocalStorage'
import { IAddress, AddressSearchResult } from '~/types'
import { EvmAddress } from '~/utils/address'

const MAX_SEARCH_HISTORY = 10

// Recents are stored per workspace so history never travels across orgs. No key
// (logged out / org not hydrated yet) means no reads and no writes.
export const getSearchHistoryStorageKey = (activeOrgId: string | undefined) =>
  activeOrgId ? `splits.searchHistory.${activeOrgId}` : undefined

const parseHistory = (raw: string): AddressSearchResult[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as AddressSearchResult[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// Read-modify-write against current storage contents, so a stale render can't
// copy one workspace's history into another workspace's bucket.
const updateHistory = (
  storageKey: string,
  updater: (history: AddressSearchResult[]) => AddressSearchResult[],
) =>
  updateSyncedLocalStorage(storageKey, (raw) =>
    JSON.stringify(updater(parseHistory(raw))),
  )

export const useAddressSearchHistory = (): {
  searchHistory: AddressSearchResult[]
  searchPlaceholder: AddressSearchResult[]
  isInSearchHistory: (result: AddressSearchResult) => boolean
  addToSearchHistory: (result: AddressSearchResult) => void
  removeFromSearchHistory: (address: IAddress) => void
} => {
  const activeOrgId = useActiveOrgId()
  const storageKey = getSearchHistoryStorageKey(activeOrgId)
  const rawHistory = useSyncedLocalStorage(storageKey)
  const storedHistory = useMemo(() => parseHistory(rawHistory), [rawHistory])

  const addressBookData = useAddressBookData()
  // Automations included so (a) automation recents survive the filter below and
  // (b) the query key matches useAddressSearchData's, deduping the fetch.
  const { smartAccounts, isLoading: isSmartAccountsLoading } = useSmartAccounts(
    { includeAutomations: true },
  )
  const { org, isFetchedAfterMount: isOrgFetchedAfterMount } = useActiveOrg()

  const internalAddressSet = useMemo(
    () =>
      new Set(
        smartAccounts.map((account) => EvmAddress.format(account.address)),
      ),
    [smartAccounts],
  )

  const bankAccountIdSet = useMemo(
    () =>
      new Set(
        (org?.bridge?.externalAccounts ?? []).map(
          (account) => account.externalAccountId,
        ),
      ),
    [org?.bridge?.externalAccounts],
  )

  const searchPlaceholder = useMemo(
    () =>
      addressBookData.map((data) => ({
        address: data.address,
        orgLabel: data.label,
      })),
    [addressBookData],
  )

  // Keeps recents in sync at read time: internal results only show while the
  // smart account is still active, bank results only while the org still has
  // the bank account. Filtering the view (instead of pruning storage) means a
  // stale render can never write another workspace's entries into this bucket.
  const searchHistory = useMemo(
    () =>
      storedHistory.filter((result) => {
        if (result.bankAccount) {
          // The org's initialData omits `bridge`, so bank data is only
          // trustworthy after a real fetch this mount.
          if (!isOrgFetchedAfterMount) return true
          return bankAccountIdSet.has(result.bankAccount.externalAccountId)
        }
        if (!result.smartAccountName) return true
        if (isSmartAccountsLoading) return true
        return internalAddressSet.has(EvmAddress.format(result.address))
      }),
    [
      bankAccountIdSet,
      internalAddressSet,
      isOrgFetchedAfterMount,
      isSmartAccountsLoading,
      storedHistory,
    ],
  )

  const addToSearchHistory = useCallback(
    (result: AddressSearchResult) => {
      if (!storageKey) return
      updateHistory(storageKey, (history) =>
        uniqBy([result, ...history], 'address').slice(0, MAX_SEARCH_HISTORY),
      )
    },
    [storageKey],
  )

  // Check if AddressSearchResult is already in search history, all same fields
  const isInSearchHistory = useCallback(
    (result: AddressSearchResult) =>
      storedHistory.some((item) => isEqual(item, result)),
    [storedHistory],
  )

  const removeFromSearchHistory = useCallback(
    (address: IAddress) => {
      if (!storageKey) return
      updateHistory(storageKey, (history) =>
        history.filter((item) => item.address !== address),
      )
    },
    [storageKey],
  )

  return useMemo(
    () => ({
      searchHistory,
      searchPlaceholder,
      isInSearchHistory,
      addToSearchHistory,
      removeFromSearchHistory,
    }),
    [
      searchHistory,
      searchPlaceholder,
      isInSearchHistory,
      addToSearchHistory,
      removeFromSearchHistory,
    ],
  )
}

export const useUpdateAddressSearchHistory = ({
  address,
  chainId,
}: {
  address: IAddress
  chainId?: SupportedChainId
}) => {
  const displayName = useDisplayName(address, chainId)
  const {
    addressSearchHistory: { isInSearchHistory, addToSearchHistory },
  } = useAuth()

  const ensAvatar = useEnsAvatar({ name: displayName.ens })

  // Memoize result to prevent re-creation and infinite loop
  const result: AddressSearchResult = useMemo(
    () => ({
      address,
      ens: displayName.ens
        ? { name: displayName.ens, avatar: ensAvatar.avatar ?? undefined }
        : undefined,
      farcaster: displayName.farcaster,
      orgLabel: displayName.orgLabel,
      smartAccountName: displayName.smartAccountName,
    }),
    [
      address,
      displayName.ens,
      displayName.farcaster,
      displayName.orgLabel,
      displayName.smartAccountName,
      ensAvatar.avatar,
    ],
  )

  const previousResult = usePrevious(result)

  useEffect(() => {
    const hasFields =
      result.ens ||
      result.farcaster ||
      result.orgLabel ||
      result.smartAccountName

    if (
      !isInSearchHistory(result) &&
      hasFields &&
      !isEqual(result, previousResult)
    ) {
      addToSearchHistory(result)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, result])
}
